import { Component, HostListener, inject, Input, OnDestroy } from '@angular/core';
import { ControlGroup } from '../../models/controls';
import { AuthService } from '../../services/auth.service';
import { QuickCustomizationService } from '../../services/quick-customization.service';
import { ConfigService } from '../../services/config.service';
import { PosthogService } from '../../services/posthog.service';
import { RestaurantService } from '../../services/restaurant.service';
import { NotificationService } from '../../services/notification.service';
import { WebPushService } from '../../services/web-push.service';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { distinctUntilChanged, filter, map } from 'rxjs';
import { CommonModule } from '@angular/common';
import {
  isPlatformSupportUser,
  isRouteAllowedForPlatformSupport,
} from '../../utils/user-role.util';
import {
  isLimitedAccessStatus,
  PUBLISHED_STATUS_HIDDEN_QUICK_ADD_GUARDS,
} from '../../utils/restaurant-status.util';
import { isQuickAddGuardAllowedForPlan } from '../../utils/subscription-plan-menu.util';
import { isPosManaged, isPosNotSelected } from '../../utils/pos-provider.util';
import { FEATURE_FLAG_KEYS, FeatureFlag, isFeatureActive } from '../../utils/feature-flag.util';
import { HoursStatus, closureChipLabel } from '../../utils/open-status.util';
import { DEFAULT_RESTAURANT_TIME_ZONE } from '../../services/reservation-time.util';

/** The header's POS badge: logo and name per provider. */
const POS_BADGES: Record<string, { label: string; logo: string }> = {
  fnf: { label: 'Fork & Find', logo: 'assets/forknfind.png' },
  clover: { label: 'Clover', logo: 'assets/clover.png' },
  square: { label: 'Square', logo: 'assets/square.webp' },
  swirepay: { label: 'Swirepay', logo: 'assets/swirepay.png' },
};

const DEFAULT_USER_IMG_URL = './../../../assets/default/profile.png';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterLink, CommonModule],
  templateUrl: './header.component.html',
  styleUrl: './header.component.css',
})
export class HeaderComponent implements OnDestroy {
  configService = inject(ConfigService);
  private router = inject(Router);
  private notificationService = inject(NotificationService);
  private webPush = inject(WebPushService);

  /** Unread inbox count on the bell; the inbox page keeps it current. */
  readonly unreadCount$ = this.notificationService.unreadCount$;
  private quickCustomization = inject(QuickCustomizationService);
  private route = inject(ActivatedRoute);
  isExpanded: boolean = true;
  login_user_img_url: string = DEFAULT_USER_IMG_URL;
  eyebrow: string = '';
  pageTitle: string = '';
  userName: string = '';
  userRole: string = '';
  currentPlanName: string | null = null;
  restaurantPosProvider: string = 'fnf';

  headerControl: ControlGroup = {
    id: '1',
    name: '',
    controls: [
      {
        id: '1',
        name: '',
        iconName: 'search-icon',
      },
      {
        id: '2',
        name: '',
        iconName: 'notification-icon',
      },
      {
        id: '3',
        name: '',
        iconName: 'dark-mode-icon',
      },
    ],
  };
  @Input() activeTab!: string;

  private authService = inject(AuthService);
  private posthogService = inject(PosthogService);
  private restaurantService = inject(RestaurantService);

  ngOnInit() {
    // The bell's number. Push keeps it live where the browser allows it; the
    // poll is the floor, for browsers or people that refuse notifications.
    this.notificationService.startPolling();
    void this.webPush.start();

    let sidebar: any;
    sidebar = document.querySelector('.sidebar');

    let navContent: any;
    navContent = document.querySelector('.nav-content');

    let homeBody: any;
    homeBody = document.querySelector('.home-body');

    let mainSection: any;
    mainSection = document.querySelector('.main-section');

    let sidebarBtn: any;
    sidebarBtn = document.querySelector('.toggle-icon');
    if (sidebarBtn) {
      sidebarBtn.addEventListener('click', () => {
        sidebar.classList.toggle('collapsed');
        navContent.classList.toggle('expand');
        homeBody.classList.toggle('expand');
        mainSection.classList.toggle('expand');
        this.isExpanded = this.isExpanded ? false : true;
        this.configService.isSidebarExpanded$.next(this.isExpanded);
      });
    }

    if (localStorage.getItem('user')) {
      let user: any = localStorage.getItem('user');
      if (user) {
        let jsonUser = JSON.parse(user);
        this.login_user_img_url = jsonUser.img_path?.trim() || DEFAULT_USER_IMG_URL;
        this.userName =
          [jsonUser.first_name, jsonUser.last_name].filter(Boolean).join(' ') ||
          jsonUser.email ||
          '';
        this.userRole = (jsonUser.user_role || '')
          .split('_')
          .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ');
      }
    }

    // Page heading (eyebrow + title) comes from the active route's `data`,
    // so every page gets the design's header block without owning one.
    this.updatePageHeading();
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
      this.updatePageHeading();
      this.refreshHoursStatus();
    });

    // The closure chip: read once a restaurant is loaded (or switched), again
    // on navigation at most once a minute, and when the closure ends.
    this.restaurantService.hoursStatus$.subscribe((status) => this.showClosure(status));
    this.restaurantService.currentRestaurantDetails$
      .pipe(
        map((restaurant: any) => restaurant?.restaurant_id ?? null),
        distinctUntilChanged(),
      )
      .subscribe((restaurantId: string | null) => {
        if (restaurantId) {
          this.refreshHoursStatus(true);
        } else {
          this.restaurantService.hoursStatus$.next(null);
        }
      });

    this.authService.updatedUserProfile$.subscribe((profile: any) => {
      if (profile && 'img_path' in profile) {
        this.login_user_img_url = profile.img_path?.trim() || DEFAULT_USER_IMG_URL;
      }
    });

    this.restaurantService.currentPlanName$.subscribe((planName: string | null) => {
      this.currentPlanName = planName;
    });

    this.restaurantService.restaurantPosProvider$.subscribe((posProvider: any) => {
      this.restaurantPosProvider = posProvider;
    });

    this.restaurantService.restaurantFeatureFlags$.subscribe((flags: FeatureFlag[] | null) => {
      this.canOpenHardware = isFeatureActive(flags, FEATURE_FLAG_KEYS.HARDWARE);
    });
  }

  /** Whether the Hardware page (where the POS is chosen) is reachable. */
  canOpenHardware = false;

  /** "Closed until 6:00 PM" while a temporary closure is in force (FNF-1612). */
  closureChip: string | null = null;
  private lastStatusRead = 0;
  private closureEndTimer: ReturnType<typeof setTimeout> | undefined;

  ngOnDestroy() {
    clearTimeout(this.closureEndTimer);
  }

  private refreshHoursStatus(force = false) {
    if (!this.restaurantService.currentRestaurantDetails$.value) {
      return;
    }
    if (!force && Date.now() - this.lastStatusRead < 60_000) {
      return;
    }
    this.lastStatusRead = Date.now();
    this.restaurantService.refreshHoursStatus().subscribe();
  }

  private showClosure(status: HoursStatus | null) {
    const timeZone =
      this.restaurantService.currentRestaurantDetails$.value?.time_zone ||
      DEFAULT_RESTAURANT_TIME_ZONE;
    this.closureChip = closureChipLabel(status, timeZone);
    clearTimeout(this.closureEndTimer);
    const until = status?.temporary_closure?.closed_until;
    const wait = until ? new Date(until).getTime() - Date.now() : null;
    // setTimeout can't wait past ~24.8 days; navigation picks those up.
    if (wait !== null && wait < 2_147_483_647) {
      this.closureEndTimer = setTimeout(
        () => this.refreshHoursStatus(true),
        Math.max(wait, 0) + 1000,
      );
    }
  }

  /** No POS chosen yet: the header offers Configure instead of a badge. */
  get posNotSelected(): boolean {
    return isPosNotSelected(this.restaurantPosProvider);
  }

  /** Logo and name for the chosen POS; null when unknown or none. */
  get posBadge(): { label: string; logo: string } | null {
    const key = String(this.restaurantPosProvider || '').toLowerCase();
    return POS_BADGES[key] ?? null;
  }

  /** Configure goes to the Hardware page, or Home (whose banner can enable
   * Fork & Find) when the HARDWARE flag keeps that page closed. */
  get configurePosLink(): string {
    return this.canOpenHardware ? '/main/hardware' : '/main/dashboard';
  }

  /**
   * Hides header shortcuts a platform support user is not allowed to open, so
   * the dropdowns never offer a link the route guard would bounce. Also hides
   * Quick Add shortcuts (table/staff/station/zone) for a restaurant that's
   * published but not yet fully activated, or that isn't on a plan tier that
   * includes them (Table/Staff/Zone/Station are Premium-only).
   */
  canAccess(route: string): boolean {
    if (isPlatformSupportUser() && !isRouteAllowedForPlatformSupport(route)) {
      return false;
    }
    if (
      isLimitedAccessStatus(this.restaurantService.currentRestaurantDetails$.value?.status) &&
      PUBLISHED_STATUS_HIDDEN_QUICK_ADD_GUARDS.includes(route)
    ) {
      return false;
    }
    if (!isQuickAddGuardAllowedForPlan(route, this.currentPlanName)) {
      return false;
    }
    return true;
  }

  // -------------------------------------------------------------- quick add

  quickAddOpen = false;

  /**
   * Each card goes straight to the form that creates the thing, rather than
   * the list it lives on. `guard` is the route canAccess() checks, which is
   * the list route even where the link is the form.
   */
  readonly quickAddItems: {
    key: string;
    label: string;
    hint: string;
    icon: string;
    link: string | null;
    guard?: string;
  }[] = [
    {
      key: 'table',
      label: 'Table',
      hint: 'Add a new dining table',
      icon: 'bx bx-grid-alt',
      link: '/add-table',
      guard: 'table',
    },
    {
      key: 'menu',
      label: 'Menu',
      hint: 'Create a new menu item',
      icon: 'bx bx-book-open',
      link: '/main/add-menu',
    },
    {
      key: 'staff',
      label: 'Staff',
      hint: 'Invite a new staff member',
      icon: 'bx bx-user-plus',
      link: '/add-staff',
      guard: 'staffs',
    },
    {
      key: 'zone',
      label: 'Zone',
      hint: 'Define a new dining zone',
      icon: 'bx bx-map-pin',
      link: '/add-zone',
      guard: 'zone',
    },
    {
      key: 'type',
      label: 'Type & Category',
      hint: 'Add types and categories',
      icon: 'bx bx-purchase-tag',
      link: '/add-type',
    },
    {
      key: 'station',
      label: 'Station',
      hint: 'Set up a new station',
      icon: 'bx bx-desktop',
      link: '/add-station',
      guard: 'station',
    },
    {
      // Opens the sheet over the current page rather than routing anywhere.
      key: 'customization',
      label: 'Customization',
      hint: 'Create new customization',
      icon: 'bx bx-cog',
      link: null,
      guard: 'customization',
    },
  ];

  get visibleQuickAddItems() {
    return this.quickAddItems.filter((item) => this.canAccess(item.guard ?? item.key));
  }

  /**
   * Menu, Type & Category, and Customization schemas come from Clover/Square
   * once the restaurant is on one of those POS providers, so manual creation
   * is disabled rather than hidden (same "cz-disabled" treatment as the
   * Customizations and Types & Categories pages, and the redirect add-menu
   * already does for a new menu item).
   */
  isQuickAddDisabled(item: { key: string }): boolean {
    return (
      (item.key === 'menu' || item.key === 'type' || item.key === 'customization') &&
      isPosManaged(this.restaurantPosProvider)
    );
  }

  /**
   * Most items are a route; Customization has no page of its own and opens
   * its sheet over whatever the user is looking at, so nothing navigates.
   */
  runQuickAdd(item: { key: string; link: string | null }) {
    if (this.isQuickAddDisabled(item)) {
      return;
    }
    this.closeQuickAdd();
    if (item.link) {
      this.router.navigateByUrl(item.link);
      return;
    }
    this.quickCustomization.openNew().subscribe();
  }

  toggleQuickAdd() {
    this.quickAddOpen = !this.quickAddOpen;
  }

  closeQuickAdd() {
    this.quickAddOpen = false;
  }

  /**
   * Anything outside the panel closes it — including the header's own other
   * menus, which sit inside this component, so testing against the whole host
   * would leave the panel open behind them.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.quickAddOpen) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (!target?.closest('.qa-wrap')) {
      this.closeQuickAdd();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    this.closeQuickAdd();
  }

  private updatePageHeading() {
    let snapshot = this.route.snapshot;
    while (snapshot.firstChild) {
      snapshot = snapshot.firstChild;
    }
    this.eyebrow = snapshot.data['eyebrow'] || '';
    this.pageTitle = snapshot.data['title'] || '';
  }

  logout() {
    this.posthogService.reset();
    this.authService.logout();
  }

  goToRedirectPage() {}

  onImageError(event: Event): void {
    (event.target as HTMLImageElement).src = './../../../assets/default/profile.png';
  }
}
